import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { Button, Icon, SettingsGroup, SettingsItemAutocomplete, SettingsItemTextField } from '@elipssolution/harfang';
import { mdiAlertCircle, mdiPencil, mdiPlus, mdiSync } from '@mdi/js';
import { Stack, Typography, styled } from '@mui/material';
import getConfig from 'next/config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import CustomerFileCodeField from './CustomerFileCodeField';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import PermissionWall from '../../../../components/PermissionWall';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { DomainType } from '../../../../types/domain';
import { PermissionEnum } from '../../../../types/permission';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import {
	CREATE_CUSTOMER_FILE,
	CreateCustomerFileType,
	FetchSettingsCustomerFileResultType,
	REMOVE_CUSTOMER_FILE,
	RemoveCustomerFileType,
	UPDATE_CUSTOMER_FILE,
	UpdateCustomerFileType,
} from '../../../api/customerFile';
import { FETCH_DOMAINS, FetchDomainsType } from '../../../api/domain';
import { SYNCHRONIZE_CUSTOMER_FILE } from '../../../api/synchonization';
import { useSession } from '../../../components/session/SessionContext';
import { AccountingConnectorType } from '../../../types/accountingConnector';
import { CustomerFileDocumentConnectorType } from '../../../types/documentConnector';
import { PublicRuntimeConfigType } from '../../../types/publicRuntimeConfig';
import { CustomerFileFormType } from '../../../types/settingsCustomerFile';
import useAccountingConnectorsDataSource from '../hooks/useAccountingConnectorsDataSource';
import useDocumentConnectorsDataSource from '../hooks/useDocumentConnectorsDataSource';

const ErrorWrapper = styled('div')(({ theme }) => ({
	overflow: 'auto',

	minHeight: 100,
	minWidth: '50%',
	maxWidth: '75%',

	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',

	padding: theme.spacing(1),
	gap: theme.spacing(1),
	margin: '16px auto 0 auto',

	color: theme.palette.error.main,
	backgroundColor: `${theme.palette.error.main}1A`,
	borderRadius: theme.shape.borderRadius,
}));

const ButtonWrapper = styled('div')({
	display: 'flex',
	flexDirection: 'row-reverse',
	justifyContent: 'space-between',
});

type CustomerFileFormProps = {
	customerFile?: FetchSettingsCustomerFileResultType['customerFile'];
	onCustomerFileChange: (customerFile: Pick<FetchSettingsCustomerFileResultType['customerFile'], 'id'>) => void;
};

const CustomerFileForm = ({ customerFile, onCustomerFileChange }: CustomerFileFormProps) => {
	const { id, code, domain, name, accountingConnector, documentConnector } = customerFile ?? {};

	const {
		publicRuntimeConfig: { standaloneModule },
	} = getConfig() as PublicRuntimeConfigType;

	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { confirmAction } = useConfirmationDialogContext();

	const { refetch: refetchSession, customerFile: sessionCustomerFile } = useSession();

	const {
		control,
		formState: { isDirty, isValid },
		handleSubmit,
		setValue,
		watch,
		reset,
	} = useForm<CustomerFileFormType>({
		mode: 'onChange',
	});

	const [errorMessage, setErrorMessage] = useState('');
	const [isCreateCustomerFileSucceeded, setIsCreateCustomerFileSucceeded] = useState(false);
	const [isUpdateCustomerFileSucceeded, setIsUpdateCustomerFileSucceeded] = useState(false);

	const [fetchDomains] = useLazyQuery<FetchDomainsType>(FETCH_DOMAINS);

	const domainsDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			items: DomainType[];
			count: number;
		}> => {
			const { data, error } = await fetchDomains({
				variables: {
					page: {
						limit,
						offset,
					},
					search,
				},
			});

			if (error) {
				throw error;
			}

			const {
				domains: { count = 0, items = [] },
			} = data ?? {
				domains: {},
			};

			if (!watch('domain')) {
				setValue('domain', items.find(({ isDefault }) => isDefault) as DomainType);
			}

			return {
				count,
				items,
			};
		},
		[fetchDomains, setValue, watch],
	);

	const { dataSource: accountingConnectorsDataSource } = useAccountingConnectorsDataSource();

	const { dataSource: documentConnectorsDataSource } = useDocumentConnectorsDataSource();

	const [createCustomerFile, { loading: isCreateCustomerFileLoading }] = useMutation<CreateCustomerFileType>(
		CREATE_CUSTOMER_FILE,
		{
			onCompleted: ({ createCustomerFile: customerFileResult }) => {
				onCustomerFileChange(customerFileResult);
				setIsCreateCustomerFileSucceeded(true);
				setTimeout(() => setIsCreateCustomerFileSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [updateCustomerFile, { loading: isUpdateCustomerFileLoading }] = useMutation<UpdateCustomerFileType>(
		UPDATE_CUSTOMER_FILE,
		{
			onCompleted: ({ updateCustomerFile: customerFileResult }) => {
				onCustomerFileChange(customerFileResult);
				setIsUpdateCustomerFileSucceeded(true);
				setTimeout(() => setIsUpdateCustomerFileSucceeded(false), SUCCESS_ANIMATION_DELAY);
			},
		},
	);

	const [removeCustomerFile, { loading: isRemoveCustomerFileLoading }] = useMutation<RemoveCustomerFileType>(
		REMOVE_CUSTOMER_FILE,
		{
			onCompleted: ({ removeCustomerFile: customerFileResult }) => {
				if (sessionCustomerFile?.id === customerFileResult.id) {
					refetchSession().catch((error) => {
						throw error;
					});
				}

				back();
			},
			onError: (error: ApolloError) =>
				setErrorMessage(generateErrorInformations({ error, resource: 'removeCustomerFile' })?.message),
		},
	);

	const [synchronizeCustomerFile] = useMutation(SYNCHRONIZE_CUSTOMER_FILE, {
		variables: { customerFileId: id },
	});

	const isLoading = useMemo(
		() => isCreateCustomerFileLoading || isUpdateCustomerFileLoading,
		[isCreateCustomerFileLoading, isUpdateCustomerFileLoading],
	);

	const isMutationButtonDisabled = useMemo(
		() => isRemoveCustomerFileLoading || !isValid || !isDirty,
		[isDirty, isRemoveCustomerFileLoading, isValid],
	);

	const startIcon = useMemo(() => <Icon path={customerFile ? mdiPencil : mdiPlus} />, [customerFile]);

	const onSubmit = useCallback(
		({
			accountingConnector: { code: accountingConnectorCode },
			documentConnector: formDocumentConnector,
			code: formCodeValue,
			domain: { id: domainId },
			name: formNameValue,
		}: CustomerFileFormType) =>
			(customerFile
				? updateCustomerFile({
						variables: {
							updateCustomerFileInput: {
								id: customerFile.id,
								name: formNameValue,
								documentConnectorCode: formDocumentConnector?.code,
								accountingConnectorCode,
								domainId,
							},
						},
				  })
				: createCustomerFile({
						variables: {
							createCustomerFileInput: {
								code: formCodeValue,
								documentConnectorCode: formDocumentConnector?.code,
								accountingConnectorCode,
								name: formNameValue,
								domainId,
							},
						},
				  })
			).catch((error: ApolloError) => {
				const { code: errorCode, message } = generateErrorInformations({
					error,
					resource: customerFile ? 'updateCustomerFile' : 'createCustomerFile',
				});

				setErrorMessage(
					errorCode === 409
						? 'Le code du dossier client que vous avez saisi existe déjà. Si ce dernier ne vous est pas attribué, veuillez contacter votre responsable.'
						: message,
				);
			}),
		[customerFile, updateCustomerFile, createCustomerFile],
	);

	const synchronize = useCallback(() => synchronizeCustomerFile(), [synchronizeCustomerFile]);

	const submitButtonLabel = useMemo(() => {
		if (customerFile) {
			if (isCreateCustomerFileSucceeded) return 'Dossier ajouté';
			if (isUpdateCustomerFileSucceeded) return 'Dossier modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isCreateCustomerFileSucceeded, isUpdateCustomerFileSucceeded, customerFile]);

	const handleCustomerFileRemoveButtonClick = async () => {
		if (!name) return undefined;

		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le dossier ${name} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		return removeCustomerFile({
			variables: {
				removeCustomerFileId: customerFile?.id,
			},
		});
	};

	useEffect(() => {
		reset({ code, domain, name, accountingConnector, documentConnector });
	}, [customerFile, code, accountingConnector, documentConnector, domain, name, reset]);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${customerFile ? 'modification' : 'création'} du dossier client ?`,
		);
	}, [customerFile, isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange]);

	return (
		<>
			<SettingsGroup>
				<CustomerFileCodeField
					control={control}
					disabled={!!customerFile}
					initialValue={code}
					onCustomerFileChange={onCustomerFileChange}
				/>

				<Controller
					name="name"
					control={control}
					render={({ field, fieldState: { error } }) => (
						<SettingsItemTextField
							{...field}
							description="Nom du dossier client."
							invalid={!!error}
							label="Nom"
							required
						/>
					)}
					rules={{ required: true }}
				/>
				<Controller
					name="domain"
					control={control}
					render={({ field }) => (
						<SettingsItemAutocomplete<DomainType>
							{...field}
							disableClearable
							dataSource={domainsDataSource}
							description="Domaine attaché au dossier client."
							getOptionLabel={(option) => option?.name || ''}
							label="Domaine"
						/>
					)}
					rules={{ required: true }}
				/>

				<Controller
					control={control}
					render={({ field }) => (
						<SettingsItemAutocomplete<
							Pick<AccountingConnectorType, 'name'> & {
								code: AccountingConnectorType['code'] | null;
							}
						>
							{...field}
							disableClearable
							dataSource={accountingConnectorsDataSource}
							getOptionLabel={(option) => option?.name || ''}
							label="Connecteur comptable"
							required
						/>
					)}
					name="accountingConnector"
					rules={{ required: true }}
				/>

				{(!standaloneModule || standaloneModule === 'document') && (
					<Controller
						control={control}
						render={({ field }) => (
							<SettingsItemAutocomplete<
								Pick<CustomerFileDocumentConnectorType, 'name'> & {
									code: CustomerFileDocumentConnectorType['code'] | null;
								}
							>
								{...field}
								disableClearable
								dataSource={documentConnectorsDataSource}
								getOptionLabel={(option) => option?.name || ''}
								label="Connecteur GED"
								required
							/>
						)}
						name="documentConnector"
						rules={{ required: true }}
					/>
				)}
			</SettingsGroup>
			<ButtonWrapper>
				<Stack flexDirection="row" gap={1}>
					{customerFile && (
						<PermissionWall permissionCodes={[PermissionEnum.ACCOUNTING_SYNCHRONIZE]}>
							<Button onClick={synchronize} startIcon={<Icon path={mdiSync} />}>
								Synchroniser
							</Button>
						</PermissionWall>
					)}

					<Button
						disabled={isMutationButtonDisabled}
						onClick={handleSubmit(onSubmit)}
						startIcon={startIcon}
						variant="contained"
					>
						{submitButtonLabel}
					</Button>
				</Stack>

				{customerFile && (
					<Button color="error" disabled={isLoading} onClick={handleCustomerFileRemoveButtonClick} variant="outlined">
						Supprimer
					</Button>
				)}
			</ButtonWrapper>

			{errorMessage && (
				<ErrorWrapper>
					<Icon path={mdiAlertCircle} />
					<Typography>{errorMessage}</Typography>
				</ErrorWrapper>
			)}
		</>
	);
};

export default CustomerFileForm;
